import classNames from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import moment from 'moment-timezone';
import type { ReactNode } from 'react';
import { useState } from 'react';
import {
  Button,
  Container,
  Dropdown,
  Header,
  Icon,
  Label,
  List,
  Loader,
  Menu,
  Modal,
  Popup,
} from 'semantic-ui-react';

import type { Permissions } from '@models';
import { getDocsUrl } from '@routes/utils';
import { SET_ACCOUNT_UPDATE_CONTEXT_SUCCESS } from '@store/action-types';
import { logout } from '@store/actions/action-auth';
import { updateAccountContext } from '@store/actions/action-system';
import { setTopnavViewProps } from '@store/actions/action-topnav';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { showModal } from '@store/modals.slice';
import ConditionalWrap from '@components/conditional-wrap';
import DarkModeToggle from '@components/dark-mode/dark-mode-toggle';
import { useFeatureFlag } from '@components/feature-flag';
import raiseToast from '@components/toast';
import ApiKeysModal from '@features/accounts/api-keys-modal';
import { MadRobotModal } from '@features/madrobot';
import useIsAuthenticated from '@hooks/use-is-authenticated';

import { AboutDialog } from './about-dialog';
import ChangePassword from './change-password';
import RegistryList from './registry-list';
import type { WhatsNewData } from './whats-new';
import { WhatsNew } from './whats-new';
import s from './topnav.scss';

export interface TopnavProps {
  permissions: Permissions | null;
  customLinks: CustomLinks | false;
  isLicenseGood: IsLicenseGood | false;
  whatsNew: WhatsNewData | false;
  uiVersion: string;
  uiCommitSha: string | null;
  uiImageBuildTimestamp: string | null;
}

const Topnav = (props: TopnavProps) => {
  const {
    permissions,
    customLinks,
    isLicenseGood,
    whatsNew,
    uiVersion,
    uiCommitSha,
    uiImageBuildTimestamp,
  } = props;

  const auth = useAppSelector(state => state.auth);
  const isAuthenticated = useIsAuthenticated();

  const app = useAppSelector(state => state.app);
  const system = useAppSelector(state => state.system);

  const dispatch = useAppDispatch();

  // Mad Robot is gated on the feature flag *and* the admin role, mirroring the
  // server's `checkAdmin` on the config endpoints — a non-admin would only hit
  // 403s, so don't surface the entry to them.
  const canUseMadRobot = useFeatureFlag('madrobot') && !!auth.account?.isAdmin;

  const setTopnavViewPropsWrapper = (viewProps: {
    contextitm?: boolean;
    linksitm?: boolean;
    registryitm?: boolean;
    accountbtn?: boolean;
  }) => {
    dispatch(setTopnavViewProps('topnav', viewProps));
  };

  const [ctxpin, setCtxpin] = useState(false);
  const [lnkpin, setLnkpin] = useState(false);

  const showAccountSwitch = () => {
    const { topnav } = app.topnav.viewProps;

    if (!auth.account) {
      return null;
    }

    const { accounts, context, accountname } = auth.account;
    const hasAccounts =
      accounts instanceof Array &&
      accounts.filter(
        item => item.state === 'enabled' || item.state === 'disabled',
      ).length > 1;

    let itemEl = context ? (
      <Dropdown.Item
        key="ctx"
        disabled={!hasAccounts}
        selected={topnav.contextitm}
        className={s.acctMenuItem}
        icon={<Icon name="shuffle" color="teal" />}
        onMouseEnter={() => {
          setCtxpin(true);
          setLnkpin(false);
          setTopnavViewPropsWrapper({
            contextitm: true,
            linksitm: false,
          });
        }}
        onMouseLeave={() => {
          setCtxpin(false);
          setLnkpin(false);
        }}
        onClick={() => {
          setTopnavViewPropsWrapper({
            contextitm: true,
            linksitm: false,
          });
        }}
        content="Switch Account Data Context"
      />
    ) : null;

    if (itemEl && hasAccounts) {
      itemEl = (
        <Popup
          trigger={itemEl}
          position="left center"
          onMouseLeave={() => {
            setCtxpin(false);
          }}
          open={
            system.isUpdatingAccountContext === true
              ? false
              : ctxpin || topnav.contextitm
          }
          hoverable
          basic
          className={classNames(
            s.acctPopup,
            s.ctxPopup,
            'animate__animated animate__fadeIn',
          )}
        >
          <List selection>
            {accounts
              .filter(
                item => item.state === 'enabled' || item.state === 'disabled',
              )
              .map(item => (
                <List.Item
                  key={`account_${item.name}`}
                  className={s.acctListItem}
                  onMouseDown={() => {
                    if (
                      item.name !== context?.accountname &&
                      item.state === 'enabled'
                    ) {
                      setCtxpin(false);
                      setLnkpin(false);
                      dispatch(updateAccountContext({ name: item.name })).then(
                        (resp: {
                          type: string;
                          resp?: {
                            data?: {
                              account?: { context?: { isEnabled: boolean } };
                            };
                          };
                        }) => {
                          if (
                            resp &&
                            resp.type === SET_ACCOUNT_UPDATE_CONTEXT_SUCCESS
                          ) {
                            raiseToast({
                              toastId: `${item.name}:${resp.type}`,
                              message: (
                                <span>
                                  The account data context for the current
                                  session&nbsp;
                                  {item.name === accountname
                                    ? 'has been returned to account'
                                    : `has been switched to ${
                                        !resp?.resp?.data?.account?.context
                                          ?.isEnabled
                                          ? '(disabled)'
                                          : ''
                                      } account`}
                                  &nbsp;
                                  <strong>{item.name}</strong>.
                                </span>
                              ),
                              level: 'info',
                              icon:
                                item.name === accountname
                                  ? 'angle double left'
                                  : 'shuffle',
                              autoClose: 6000,
                            });
                          } else {
                            raiseToast({
                              toastId: `${item.name}:${resp.type}`,
                              message: (
                                <span>
                                  <strong>We&#39;re sorry&hellip;</strong>
                                  &nbsp;but due to a service error we were not
                                  able to make <strong>{item.name}</strong>
                                  &nbsp;the new data context.
                                </span>
                              ),
                              level: 'error',
                              autoClose: 8000,
                            });
                          }
                        },
                      );
                    } else {
                      setCtxpin(true);
                      setLnkpin(false);
                    }
                  }}
                >
                  <List.Icon
                    color="green"
                    name={
                      item.name === context?.accountname
                        ? 'check circle'
                        : 'circle outline'
                    }
                  />
                  <List.Icon
                    style={{
                      position: 'absolute',
                      color: '#fff',
                      zIndex: 0,
                    }}
                    name="circle"
                  />
                  <List.Content className={s.acctListItemContent}>
                    <Icon
                      color={item.state === 'enabled' ? 'teal' : 'grey'}
                      name={
                        item.type === 'admin' ? 'chess queen' : 'user circle'
                      }
                    />
                    {item.type !== 'admin' ? (
                      <List.Icon
                        style={{
                          position: 'absolute',
                          color: '#fff',
                          zIndex: 0,
                        }}
                        name="circle"
                      />
                    ) : null}
                    <span>
                      <span
                        className={classNames(
                          s.acctNameItem,
                          item.name === context?.accountname
                            ? s.currentCtx
                            : null,
                        )}
                      >
                        {item.name}
                      </span>
                      {item.state !== 'enabled' ? (
                        <span
                          className={classNames(
                            s.acctNameItem,
                            s.acctDisabledItem,
                          )}
                        >
                          Disabled
                        </span>
                      ) : null}
                      {item.name === accountname ? (
                        <span
                          className={classNames(
                            s.acctNameItem,
                            s.acctActualItem,
                          )}
                        >
                          Actual
                        </span>
                      ) : null}
                    </span>
                  </List.Content>
                </List.Item>
              ))}
          </List>
        </Popup>
      );
    }
    return itemEl;
  };

  const showCustomLinks = () => {
    const { topnav } = app.topnav.viewProps;

    let itemEl = customLinks ? (
      <Dropdown.Item
        // disabled={!hasAccounts}
        key="lnk"
        selected={topnav.linksitm}
        className={s.acctMenuItem}
        icon={<Icon name="external" color="teal" />}
        onMouseEnter={() => {
          setCtxpin(false);
          setLnkpin(true);
          setTopnavViewPropsWrapper({
            linksitm: true,
            contextitm: false,
          });
        }}
        onMouseLeave={() => {
          setCtxpin(false);
          setLnkpin(false);
        }}
        onClick={() => {
          setTopnavViewPropsWrapper({
            linksitm: true,
            contextitm: false,
          });
        }}
        content={
          <span className={s.acctMenuItemTitle}>{customLinks.title}</span>
        }
      />
    ) : null;

    if (itemEl) {
      itemEl = (
        <Popup
          trigger={itemEl}
          position="left center"
          onMouseLeave={() => {
            setCtxpin(false);
            setLnkpin(false);
          }}
          open={lnkpin || topnav.linksitm}
          hoverable
          basic
          className={classNames(
            s.acctPopup,
            s.lnkPopup,
            'animate__animated animate__fadeIn',
          )}
        >
          {customLinks && (
            <List selection>
              {customLinks.links.map(item => (
                <List.Item
                  key={`${item.title}_${Math.random()}`}
                  className={s.acctListItem}
                >
                  <List.Content
                    as="a"
                    rel="noopener noreferrer"
                    href={item.uri}
                    target="_blank"
                    className={s.acctListItemContent}
                  >
                    <Icon color="teal" name="external" />
                    <span>{item.title}</span>
                  </List.Content>
                </List.Item>
              ))}
            </List>
          )}
        </Popup>
      );
    }
    return itemEl;
  };

  const showPwdModal = (trigger?: ReactNode) => {
    const { account } = auth;

    return (
      <Modal
        size="mini"
        closeIcon
        trigger={
          trigger || (
            <Dropdown.Item
              key="pwd"
              disabled={account?.authType !== 'default'}
              className={s.acctMenuItem}
              icon={<Icon name="lock" color="teal" />}
              content="Change Your Password"
              onMouseEnter={() => {
                setCtxpin(false);
                setLnkpin(false);
                setTopnavViewPropsWrapper({
                  contextitm: false,
                  linksitm: false,
                });
              }}
            />
          )
        }
      >
        <Header
          style={{
            fontWeight: 100,
            backgroundColor: 'rgb(54, 130, 224)',
            border: 0,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span className={s.changePasswordHeader}>Change Your Password</span>
        </Header>
        <Modal.Content>
          <Modal.Description>
            <ChangePassword />
          </Modal.Description>
        </Modal.Content>
      </Modal>
    );
  };

  const showRegistryModal = (trigger?: ReactNode) =>
    permissions && (
      <RegistryList
        permissions={permissions}
        trigger={
          trigger || (
            <Dropdown.Item
              key="registry"
              disabled={!permissions.listRegistries}
              className={s.acctMenuItem}
              icon={<Icon name="address book outline" color="teal" />}
              content="Add / Edit Registry Credentials"
              onMouseEnter={() => {
                setCtxpin(false);
                setLnkpin(false);
                setTopnavViewPropsWrapper({
                  contextitm: false,
                  linksitm: false,
                });
              }}
              onClick={() => {
                setTopnavViewPropsWrapper({
                  registryitm: true,
                });
              }}
            />
          )
        }
      />
    );

  const { accountbtn } = app.topnav.viewProps.topnav;
  const { healthCheck } = app;
  const { isEngineVersionGood } = healthCheck;
  const engineVersion =
    isEngineVersionGood && isEngineVersionGood?.engineVersion;
  const { account } = auth;
  const isLdapUser = account && account.authType === 'ldap';

  const isSwitched =
    account &&
    account.context &&
    account.context.accountname !== account.accountname;

  return (
    <div className={s.root}>
      <Container>
        <Menu
          inverted
          borderless
          fixed="top"
          className={classNames(
            s.topNavMenu,
            isAuthenticated ? 'pace-border' : '',
          )}
        >
          <Menu.Item as="a" header href="/" className={s.logoWrapper}>
            <span className={s.logo} />
            <span
              className={classNames(
                s.releaseLabel,
                'animate__animated',
                'animate__fadeIn',
              )}
            >
              {engineVersion ? `${engineVersion} / ` : null}
              {uiVersion} UI
            </span>
          </Menu.Item>
          {isAuthenticated && whatsNew ? <WhatsNew data={whatsNew} /> : null}
          {isAuthenticated &&
          isLicenseGood &&
          !isLicenseGood.isBeforeWarning ? (
            <Menu.Item header>
              <AboutDialog
                permissions={permissions}
                isLicenseGood={isLicenseGood}
                uiCommitSha={uiCommitSha}
                uiImageBuildTimestamp={uiImageBuildTimestamp}
                whatsNew={whatsNew}
                uiVersion={uiVersion}
                trigger={
                  <div
                    className={classNames(
                      'animate__animated animate__fadeIn',
                      s.licenseWarningWrapper,
                    )}
                  >
                    <Icon color="orange" name="warning sign" />
                    <span>
                      Your license will expire&nbsp;
                      {moment(isLicenseGood.expiration).fromNow()}
                    </span>
                  </div>
                }
              />
            </Menu.Item>
          ) : null}

          {isAuthenticated && account ? (
            <Menu.Menu position="right">
              <DarkModeToggle />
              {isSwitched ? (
                <Menu.Item style={{ overflow: 'hidden' }}>
                  <Button
                    icon
                    labelPosition="left"
                    className={s.accountBtn}
                    color="orange"
                    size="tiny"
                    disabled={system.isUpdatingAccountContext}
                    onClick={() => {
                      dispatch(
                        updateAccountContext({
                          name: account.accountname,
                        }),
                      ).then((resp: { type: string }) => {
                        if (
                          resp &&
                          resp.type === SET_ACCOUNT_UPDATE_CONTEXT_SUCCESS
                        ) {
                          raiseToast({
                            toastId: `${account.accountname}:${resp.type}`,
                            message: (
                              <span>
                                The account data context for the current session
                                has been has been returned to account&nbsp;
                                <strong>{account.accountname}</strong>.
                              </span>
                            ),
                            level: 'info',
                            icon: 'angle double left',
                            autoClose: 6000,
                            dismissAll: true,
                          });
                        } else {
                          raiseToast({
                            toastId: `${account.accountname}:${resp.type}`,
                            message: (
                              <span>
                                <strong>We&#39;re sorry&hellip;</strong>
                                &nbsp;but due to a service error we were not
                                able to make&nbsp;
                                <strong>{account.accountname}</strong>
                                &nbsp;the new data context.
                              </span>
                            ),
                            level: 'error',
                            autoClose: 8000,
                            dismissAll: true,
                          });
                        }
                      });
                    }}
                  >
                    <span style={{ fontWeight: 500, marginRight: '0.25rem' }}>
                      Return to context
                    </span>
                    <strong>{account.accountname}</strong>
                    <Icon name="angle double left" />
                  </Button>
                </Menu.Item>
              ) : null}
              {account.username ? (
                <Menu.Item style={{ overflow: 'hidden' }}>
                  <Label
                    as="a"
                    onClick={() => {
                      raiseToast(false);
                      if (!ctxpin) {
                        setTopnavViewPropsWrapper({
                          accountbtn: !accountbtn,
                          contextitm: false,
                          linksitm: false,
                        });
                      }
                    }}
                    className={s.accountBtn}
                    color={account && account.isAdmin ? 'violet' : 'blue'}
                    data-testid="topnav-account-pulldown"
                  >
                    <span
                      className={
                        account && account.isAdmin ? s.rocket : s.robot
                      }
                    />
                    <span
                      style={{
                        paddingLeft: '1.25rem',
                        color: 'rgba(255,255,255,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {account && account.isAdmin ? (
                        <Icon
                          name="chess queen"
                          className={s.accountBtnAdminIcon}
                        />
                      ) : null}
                      <span
                        style={{
                          fontWeight: 500,
                          color: 'rgba(255,255,255,0.85)',
                          lineHeight: '2rem',
                        }}
                      >
                        Account
                      </span>
                      &nbsp;
                      <span style={{ lineHeight: '2rem' }}>
                        {account.accountname}
                      </span>
                    </span>
                    {account.context &&
                    (account.context.accountname !== account.accountname ||
                      system.isUpdatingAccountContext) ? (
                      <>
                        <span className={s.verticalDivider} />
                        <span
                          style={{
                            fontWeight: 500,
                            color: 'rgba(255,255,255,0.85)',
                            lineHeight: '2rem',
                          }}
                        >
                          Context
                        </span>
                        &nbsp;
                        {system.isUpdatingAccountContext ? (
                          <Loader
                            size="tiny"
                            active
                            inline
                            inverted
                            style={{
                              marginLeft: '0.25rem',
                              lineHeight: '2rem',
                            }}
                          />
                        ) : (
                          <>
                            <span style={{ lineHeight: '2rem' }}>
                              {account.context.accountname}
                            </span>
                            {!account.context.isEnabled ? (
                              <span
                                style={{
                                  fontWeight: 500,
                                  color: 'rgba(255,255,255,0.85)',
                                  marginLeft: '0.25rem',
                                  lineHeight: '2rem',
                                }}
                              >
                                (disabled)
                              </span>
                            ) : null}
                          </>
                        )}
                      </>
                    ) : null}
                    <span className={s.verticalDivider} />
                    <span
                      style={{
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.85)',
                        lineHeight: '2rem',
                      }}
                    >
                      Username
                    </span>
                    &nbsp;
                    <span style={{ lineHeight: '2rem' }}>
                      {account.authUsername}
                    </span>
                    <Icon
                      name={`caret ${accountbtn ? 'up' : 'down'}`}
                      className={s.accountBtnCaretIcon}
                    />
                  </Label>
                </Menu.Item>
              ) : null}
              <Dropdown
                open={
                  system.isUpdatingAccountContext === true
                    ? false
                    : ctxpin || lnkpin || accountbtn
                }
                icon={false}
                style={{ padding: 0, width: 0, fontSize: '0.95rem' }}
                item
                closeOnChange={false}
                onClose={() => {
                  if (!ctxpin) {
                    setTimeout(() => {
                      setTopnavViewPropsWrapper({
                        accountbtn: false,
                        contextitm: false,
                        linksitm: false,
                      });
                      setCtxpin(false);
                    }, 0);
                  }
                }}
              >
                <Dropdown.Menu
                  style={{ borderTop: 'none', borderBottomRightRadius: 0 }}
                  className={classNames(
                    'animate__animated animate__fadeIn',
                    s.acctMenu,
                  )}
                >
                  <Dropdown.Divider style={{ margin: 0 }} />
                  <AboutDialog
                    permissions={permissions}
                    isLicenseGood={isLicenseGood}
                    uiCommitSha={uiCommitSha}
                    uiImageBuildTimestamp={uiImageBuildTimestamp}
                    whatsNew={whatsNew}
                    uiVersion={uiVersion}
                    trigger={
                      <Dropdown.Item
                        key="info"
                        className={s.acctMenuItem}
                        onMouseEnter={() => {
                          setCtxpin(false);
                          setTopnavViewPropsWrapper({
                            contextitm: false,
                          });
                        }}
                        icon={<Icon name="user circle" color="teal" />}
                        content="About Anchore Enterprise Client"
                        data-test="about-client-modal-trigger"
                      />
                    }
                  />
                  <Dropdown.Divider style={{ margin: 0 }} />
                  {showAccountSwitch()}
                  {showRegistryModal()}
                  {showCustomLinks()}
                  {showPwdModal()}
                  <ConditionalWrap
                    condition={isLdapUser ?? false}
                    wrap={children => (
                      <Popup trigger={children} position="right center">
                        API key creation and management is only available to
                        non-LDAP users.
                      </Popup>
                    )}
                  >
                    <Dropdown.Item
                      content={
                        <span className={s.apiKeysMenuItem}>
                          API Keys
                          {isLdapUser && (
                            <Icon
                              name="info circle"
                              color="orange"
                              className={s.infoIcon}
                            />
                          )}
                        </span>
                      }
                      className={s.acctMenuItem}
                      icon={<Icon name="key" color="teal" />}
                      key="api-keys"
                      disabled={isLdapUser ?? undefined}
                      onMouseEnter={() => {
                        setCtxpin(false);
                        setLnkpin(false);
                        setTopnavViewPropsWrapper({
                          contextitm: false,
                          linksitm: false,
                        });
                      }}
                      onClick={() =>
                        !isLdapUser &&
                        dispatch(
                          showModal({
                            type: 'apiKeys',
                            props: {
                              account: account.accountname,
                              userName: account.username,
                            },
                          }),
                        )
                      }
                    />
                  </ConditionalWrap>
                  {canUseMadRobot && (
                    <Dropdown.Item
                      key="madrobot"
                      className={s.acctMenuItem}
                      icon={<Icon name="bug" color="orange" />}
                      content="Mad Robot (Chaos)"
                      onMouseEnter={() => {
                        setCtxpin(false);
                        setLnkpin(false);
                        setTopnavViewPropsWrapper({
                          contextitm: false,
                          linksitm: false,
                        });
                      }}
                      onClick={() => dispatch(showModal({ type: 'madrobot' }))}
                    />
                  )}
                  <Dropdown.Item
                    key="docs"
                    className={s.acctMenuItem}
                    icon={<Icon name="book" color="teal" />}
                    content="Documentation"
                    as="a"
                    href={getDocsUrl()}
                    target="_blank"
                    onMouseEnter={() => {
                      setCtxpin(false);
                      setLnkpin(false);
                      setTopnavViewPropsWrapper({
                        contextitm: false,
                        linksitm: false,
                      });
                    }}
                  />
                  <Dropdown.Item
                    key="logout"
                    className={s.acctMenuItem}
                    icon={<Icon name="sign out" color="teal" />}
                    content="Log Out"
                    onMouseEnter={() => {
                      setCtxpin(false);
                      setLnkpin(false);
                      setTopnavViewPropsWrapper({
                        contextitm: false,
                        linksitm: false,
                      });
                    }}
                    onClick={() => {
                      dispatch(
                        logout({
                          server: true,
                        }),
                      );
                    }}
                  />
                </Dropdown.Menu>
              </Dropdown>
            </Menu.Menu>
          ) : null}
        </Menu>
        {permissions && <ApiKeysModal permissions={permissions} />}
        {canUseMadRobot && <MadRobotModal />}
      </Container>
    </div>
  );
};

export default withStyles(s)(Topnav);
